import React from "react";
import { DefaultButton, Depths, IContextualMenuProps, mergeStyleSets, Stack, Text, useTheme } from "@fluentui/react";
import { ITestResultWrapper } from "../TestResultPage";
import DaneValidationResultItem from "./DaneValidationResultItem";
import { IRcaDaneValidationTestResult } from "../../models/IRcaDaneValidationTestResult";
import { Utils } from "../../Utils";
import { IDaneValidationTestType, DNSSEC_AND_DANE_TEST } from "../../constants/RcaDaneValidationTestTypes";

/**
 * The test result page component
 *
 * @param props - The component properties
 * @returns The markup
 */
export const DaneValidationResultPage: React.FC<ITestResultWrapper> = ({ currentResult, downloadHtmlUrl }) => {
  const theme = useTheme();
  const classNames = mergeStyleSets({
    titleBlock: {
      width: "100%",
      backgroundColor: `var(--rca-card-background, ${theme.semanticColors.bodyBackground})`,
      border: "1px solid var(--rca-card-border, transparent)",
      borderRadius: 2,
      boxShadow: `var(--rca-card-shadow, ${Depths.depth4})`,
      paddingLeft: 20,
      paddingRight: 20,
      paddingTop: 12,
      paddingBottom: 12,
      marginBottom: 20,
      selectors: {
        [Utils.mobileQuery]: {
          flexDirection: "column",
          alignItems: "flex-start",
          "& > span": {
            [Utils.mobileQuery]: {
              paddingBottom: 20,
            },
          },
        },
      },
    },
  });

  let result: IRcaDaneValidationTestResult | null = null;
  let isValidResult = true;
  let testType: IDaneValidationTestType = DNSSEC_AND_DANE_TEST;
  const showDownloadButton = false;

  try {
    result = currentResult.AdditionalResultData as IRcaDaneValidationTestResult;
    testType = result.TestType;
  } catch {
    isValidResult = false;
  }

  const menuProps: IContextualMenuProps = {
    items: [
      {
        key: "html",
        text: "HTML file",
        iconProps: { iconName: "FileHTML" },
        onClick: (): void => {
          window.location.href = downloadHtmlUrl;
        },
      },
    ],
  };

  return (
    <Stack
      styles={{
        root: {
          width: "100%",
          padding: 20,
        },
      }}
    >
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between" className={classNames.titleBlock}>
        <Text variant="xLarge" block>
          {currentResult.ResultDescription}
        </Text>
        {showDownloadButton ? (
          <DefaultButton
            text={WebsiteStrings.DaneDownloadResult}
            splitButtonAriaLabel="See 2 options"
            aria-roledescription="split button"
            menuProps={menuProps}
            onClick={() => {
              window.location.href = downloadHtmlUrl;
            }}
          />
        ) : null}
      </Stack>

      {result && (
        <Stack role="tree" aria-label={WebsiteStrings.TestResultTable_TestDetails}>
          {result.Domains.map((domainResult) => (
            <DaneValidationResultItem key={domainResult.Domain} result={domainResult} testType={testType} />
          ))}
        </Stack>
      )}
      {!isValidResult && (
        <>
          <Stack>{currentResult.TestDescription}</Stack>
          <Stack>{currentResult.ResultDescription}</Stack>
        </>
      )}
    </Stack>
  );
};

export default DaneValidationResultPage;
